/** SUNTRONIX ONE — Order Ready Dashboard.
 *
 * Single view of every Sales Order that has SHIPPED or DELIVERED but
 * NOT been invoiced yet. Aging buckets (fresh / watch / urgent) help
 * sales / finance triage before month-end revenue recognition.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PackageCheck, ChevronLeft, RefreshCw, AlertTriangle, Clock, Zap } from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

export default function OrderReadyDashboard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/vtiger/sales-orders/dashboard/order-ready");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = data?.items || [];
  const buckets = data?.buckets || {};

  const bucketOf = (etaStr) => {
    if (!etaStr) return "fresh";
    const days = Math.floor((Date.now() - new Date(etaStr).getTime()) / 86400000);
    if (days < 0) return "fresh";
    if (days <= 7) return "watch";
    return "urgent";
  };
  const toneOf = { fresh: "bg-slate-50 text-slate-700 border-slate-200",
                          watch: "bg-amber-50 text-amber-800 border-amber-200",
                          urgent: "bg-rose-50 text-rose-800 border-rose-200" };

  return (
    <div className="space-y-4 max-w-[1500px]" data-testid="order-ready-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/commercial/crm" className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Commercial Workspace
          </Link>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-1 flex items-center gap-2">
            <PackageCheck className="h-6 w-6 text-emerald-700" />
            Order Ready — Ship &amp; Bill
          </h1>
          <div className="text-[11px] text-slate-500 mt-1">
            Sales Orders shipped or delivered but not yet invoiced. Nothing slips before month-end.
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={busy}
          data-testid="order-ready-refresh">
          <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Tile label="Open orders" value={data?.count ?? 0} icon={PackageCheck} />
        <Tile label="Open value" value={fmtINR(data?.total_open_value_inr)} tone="ok" />
        <Tile label="Watch (≤ 7 days)"
          value={buckets.watch || 0} tone="warn" icon={Clock} />
        <Tile label="Urgent (> 7 days)"
          value={buckets.urgent || 0} tone="err" icon={Zap} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {rows.length === 0 && (
          <div className="p-10 text-center text-[13px] text-slate-500">
            All orders billed. Nothing to worry about right now.
          </div>
        )}
        {rows.map((r) => {
          const b = bucketOf(r.expected_delivery_date);
          return (
            <div key={r.id}
              data-testid={`order-ready-row-${r.id}`}
              className="p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[13px] font-semibold">{r.code}</span>
                  <Badge className={"text-[10px] border " + (
                    r.status === "SHIPPED"
                      ? "bg-amber-100 text-amber-800 border-amber-200"
                      : "bg-emerald-100 text-emerald-800 border-emerald-200"
                  )}>{r.status}</Badge>
                  <Badge className={"text-[10px] border " + (toneOf[b])}>
                    {b === "fresh" ? "Not yet due" : b === "watch" ? "Watch" : "Urgent"}
                  </Badge>
                </div>
                <div className="text-[12px] text-slate-700 mt-1 truncate">
                  {r.customer_name} · {r.subject}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 flex gap-2 flex-wrap">
                  {r.customer_po_code && <span>CPO {r.customer_po_code}</span>}
                  {r.expected_delivery_date && (
                    <span>· ETA {new Date(r.expected_delivery_date).toLocaleDateString()}</span>
                  )}
                  {r.lr_number && <span>· LR {r.lr_number}</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase text-slate-500">Order value</div>
                <div className="text-[15px] font-heading font-semibold">
                  {fmtINR(r.grand_total_inr)}
                </div>
              </div>
              <Link to="/commercial/crm/sales-orders"
                className="ml-2 text-[12px] text-emerald-700 hover:underline">
                Open →
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Tile({ label, value, tone, icon: Icon }) {
  const tint = tone === "ok" ? "text-emerald-800 bg-emerald-50 border-emerald-200"
             : tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
             : tone === "err" ? "text-rose-800 bg-rose-50 border-rose-200"
             : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={"border rounded p-3 " + tint}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        <div className="text-[10px] uppercase tracking-wider">{label}</div>
      </div>
      <div className="text-2xl font-heading font-semibold mt-1">{value ?? "—"}</div>
    </div>
  );
}
